import axiosInstance from "./axiosInstance";

const BASE = "/super-admin";

/** Backend validates user_id as string on several super-admin endpoints */
const stringUserId = (id) => (id == null || id === "" ? id : String(id));

const normalizeParams = (params = {}) =>
    params.user_id == null ? params : { ...params, user_id: stringUserId(params.user_id) };

const normalizePayloadUserId = (payload = {}) =>
    payload.user_id == null ? payload : { ...payload, user_id: stringUserId(payload.user_id) };

const extractError = (err) => {
    const data = err?.response?.data;
    if (data?.errors) {
        const msgs = Object.values(data.errors).flat();
        if (msgs.length) return msgs.join(", ");
    }
    return data?.message || data?.error || err.message || "Request failed";
};

const unwrap = (res) => {
    const body = res.data;
    if (body?.success === false) {
        throw new Error(body.message || body.error || "Request failed");
    }
    return body;
};

/** Flatten credits_summary onto user for table display */
export const normalizeListUser = (user) => {
    if (!user) return user;
    const cs = user.credits_summary || {};
    const pw = cs.personal_whatsapp || {};
    const em = cs.email || {};
    const wb = cs.whatsapp_business || {};
    return {
        ...user,
        roles: user.roles || [],
        pwc: pw.pwc ?? user.pwc,
        dwpc: pw.dwpc ?? user.dwpc,
        adwpc: pw.adwpc ?? user.adwpc,
        bec: em.bec ?? user.bec,
        dbec: em.dbec ?? user.dbec,
        adbec: em.adbec ?? user.adbec,
        bwc: wb.bwc ?? user.bwc,
        dbwc: wb.dbwc ?? user.dbwc,
        adbwc: wb.adbwc ?? user.adbwc,
        bot_credits_count: wb.bot_credits_count ?? user.bot_credits_count,
    };
};

export const listUsers = async (params = {}) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users`, { params: normalizeParams(params) });
        const body = unwrap(res);
        return {
            users: (body.data || []).map(normalizeListUser),
            meta: body.meta || {},
        };
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const fetchAllUsers = async (params = {}) => {
    const perPage = params.per_page || 200;
    let page = 1;
    let all = [];
    let lastPage = 1;

    do {
        const { users, meta } = await listUsers({ ...params, page, per_page: perPage });
        all = all.concat(users);
        lastPage = meta.last_page || 1;
        page += 1;
    } while (page <= lastPage);

    return all;
};

export const getUser = async (id) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users/${stringUserId(id)}`);
        const body = unwrap(res);
        return normalizeListUser(body.data);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const createUser = async (payload) => {
    try {
        const res = await axiosInstance.post(`${BASE}/users`, payload);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateUserProfile = async (id, payload) => {
    try {
        const res = await axiosInstance.put(`${BASE}/users/${stringUserId(id)}`, payload);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateUserRoles = async (id, roles, mode = "replace") => {
    try {
        const res = await axiosInstance.put(`${BASE}/users/${stringUserId(id)}/roles`, { roles, mode });
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateAccountStatus = async (id, accountDisabled) => {
    try {
        const res = await axiosInstance.put(`${BASE}/users/${stringUserId(id)}/account-status`, {
            account_disabled: accountDisabled,
        });
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const getBotAutomationVersion = async (id) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users/${stringUserId(id)}/bot-automation-version`);
        return unwrap(res).data;
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateBotAutomationVersion = async (id, version) => {
    try {
        const res = await axiosInstance.put(`${BASE}/users/${stringUserId(id)}/bot-automation-version`, {
            bot_automation_version: version,
        });
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const getApiKey = async (id) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users/${stringUserId(id)}/api-key`);
        return unwrap(res).data;
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateApiKey = async (id, payload) => {
    try {
        const res = await axiosInstance.put(`${BASE}/users/${stringUserId(id)}/api-key`, payload);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const getCredits = async (id) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users/${stringUserId(id)}/credits`);
        return unwrap(res).data;
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateCredits = async (id, payload) => {
    try {
        const res = await axiosInstance.put(`${BASE}/users/${stringUserId(id)}/credits`, payload);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const listSubscriptions = async (params = {}) => {
    try {
        const res = await axiosInstance.get(`${BASE}/subscriptions`, { params: normalizeParams(params) });
        const body = unwrap(res);
        return { rows: body.data || [], meta: body.meta || {} };
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const getUserSubscriptions = async (id) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users/${stringUserId(id)}/subscriptions`);
        return unwrap(res).data;
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateSubscription = async (userId, service, payload) => {
    try {
        const res = await axiosInstance.put(
            `${BASE}/users/${stringUserId(userId)}/subscriptions/${service}`,
            payload
        );
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const listWbConfigs = async (params = {}) => {
    try {
        const res = await axiosInstance.get(`${BASE}/whatsapp-business-configs`, { params: normalizeParams(params) });
        const body = unwrap(res);
        return { configs: body.data || [], meta: body.meta || {} };
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const getWbConfig = async (userId) => {
    try {
        const res = await axiosInstance.get(`${BASE}/whatsapp-business-configs/${stringUserId(userId)}`);
        return unwrap(res).data;
    } catch (err) {
        if (err?.response?.status === 404) return null;
        throw new Error(extractError(err));
    }
};

export const createWbConfig = async (payload) => {
    try {
        const res = await axiosInstance.post(`${BASE}/whatsapp-business-configs`, normalizePayloadUserId(payload));
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateWbConfig = async (userId, payload) => {
    try {
        const res = await axiosInstance.put(`${BASE}/whatsapp-business-configs/${stringUserId(userId)}`, payload);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const listEmailConfigs = async (params = {}) => {
    try {
        const res = await axiosInstance.get(`${BASE}/email-configs`, { params: normalizeParams(params) });
        const body = unwrap(res);
        return { configs: body.data || [], meta: body.meta || {} };
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const getEmailConfig = async (userId) => {
    try {
        const res = await axiosInstance.get(`${BASE}/email-configs/${stringUserId(userId)}`);
        return unwrap(res).data;
    } catch (err) {
        if (err?.response?.status === 404) return null;
        throw new Error(extractError(err));
    }
};

export const createEmailConfig = async (payload) => {
    try {
        const res = await axiosInstance.post(`${BASE}/email-configs`, normalizePayloadUserId(payload));
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateEmailConfig = async (userId, payload) => {
    try {
        const res = await axiosInstance.put(`${BASE}/email-configs/${stringUserId(userId)}`, payload);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const deleteEmailConfig = async (userId) => {
    try {
        const res = await axiosInstance.delete(`${BASE}/email-configs/${stringUserId(userId)}`);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

// --- AWS SES accounts (super-admin only) ---

export const listAwsSesAccounts = async (params = {}) => {
    try {
        const res = await axiosInstance.get(`${BASE}/aws-ses-accounts`, { params: normalizeParams(params) });
        const body = unwrap(res);
        return { accounts: body.data || [], meta: body.meta || {} };
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const getAwsSesAccount = async (id) => {
    try {
        const res = await axiosInstance.get(`${BASE}/aws-ses-accounts/${id}`);
        return unwrap(res).data;
    } catch (err) {
        if (err?.response?.status === 404) return null;
        throw new Error(extractError(err));
    }
};

export const createAwsSesAccount = async (payload) => {
    try {
        const res = await axiosInstance.post(`${BASE}/aws-ses-accounts`, payload);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const updateAwsSesAccount = async (id, payload) => {
    try {
        const res = await axiosInstance.put(`${BASE}/aws-ses-accounts/${id}`, payload);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const deleteAwsSesAccount = async (id) => {
    try {
        const res = await axiosInstance.delete(`${BASE}/aws-ses-accounts/${id}`);
        return unwrap(res);
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const getServiceStatus = async () => {
    try {
        const res = await axiosInstance.get(`${BASE}/service-status`);
        return unwrap(res).data;
    } catch (err) {
        throw new Error(extractError(err));
    }
};

export const listUnhealthyServices = async (params = {}) => {
    try {
        const res = await axiosInstance.get(`${BASE}/service-status/unhealthy`, { params: normalizeParams(params) });
        const body = unwrap(res);
        return { rows: body.data || [], meta: body.meta || {} };
    } catch (err) {
        throw new Error(extractError(err));
    }
};

const throwApiError = (err, fallback) => {
    const error = new Error(extractError(err) || fallback);
    error.status = err?.response?.status;
    throw error;
};

const pageFrom = (body) => {
    const payload = body?.data;
    if (payload && Array.isArray(payload.data)) {
        return {
            rows: payload.data,
            page: payload.current_page || 1,
            lastPage: payload.last_page || 1,
            total: payload.total ?? payload.data.length,
        };
    }
    if (Array.isArray(payload)) {
        return {
            rows: payload,
            page: body?.meta?.current_page || 1,
            lastPage: body?.meta?.last_page || 1,
            total: body?.meta?.total ?? payload.length,
        };
    }
    return { rows: [], page: 1, lastPage: 1, total: 0 };
};

export const getUserBillingType = async (id) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users/${stringUserId(id)}/billing-type`);
        return unwrap(res).data;
    } catch (err) {
        throwApiError(err, "Could not load billing type");
    }
};

export const updateUserBillingType = async (id, billingType) => {
    try {
        const res = await axiosInstance.put(`${BASE}/users/${stringUserId(id)}/billing-type`, {
            billing_type: billingType,
        });
        return unwrap(res);
    } catch (err) {
        throwApiError(err, "Could not update billing type");
    }
};

export const createAdminCreditTopup = async (id, creditAmount) => {
    try {
        const res = await axiosInstance.post(`${BASE}/users/${stringUserId(id)}/credit-topups`, {
            credit_amount: creditAmount,
        });
        return unwrap(res);
    } catch (err) {
        throwApiError(err, "Could not add credits");
    }
};

export const createAdminCreditDebit = async (id, creditAmount) => {
    try {
        const res = await axiosInstance.post(`${BASE}/users/${stringUserId(id)}/credit-adjustments`, {
            credit_amount: creditAmount,
        });
        return unwrap(res);
    } catch (err) {
        throwApiError(err, "Could not remove credits");
    }
};

export const listAdminBillingTransactions = async (id, params = {}) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users/${stringUserId(id)}/billing-transactions`, { params });
        return pageFrom(unwrap(res));
    } catch (err) {
        throwApiError(err, "Could not load transactions");
    }
};

export const listAdminCreditTopups = async (id, params = {}) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users/${stringUserId(id)}/credit-topups`, { params });
        return pageFrom(unwrap(res));
    } catch (err) {
        throwApiError(err, "Could not load top-ups");
    }
};

export const listCreditRates = async () => {
    try {
        const res = await axiosInstance.get(`${BASE}/credit-rates`);
        const body = unwrap(res);
        const payload = body.data;
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    } catch (err) {
        throwApiError(err, "Could not load credit rates");
    }
};

export const updateCreditRate = async (id, credits) => {
    try {
        const res = await axiosInstance.put(`${BASE}/credit-rates/${id}`, { credits });
        return unwrap(res);
    } catch (err) {
        throwApiError(err, "Could not save the rate");
    }
};

export const getUserServiceStatus = async (id) => {
    try {
        const res = await axiosInstance.get(`${BASE}/users/${stringUserId(id)}/service-status`);
        return unwrap(res).data;
    } catch (err) {
        throw new Error(extractError(err));
    }
};
